import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2, Search, Eye, CheckCircle, XCircle, Clock, FileText,
  ChevronRight, AlertTriangle, Loader2, RefreshCw, History,
  ChevronUp, ChevronDown, ChevronsUpDown, ArrowRight, Info, Settings,
  Plus, Download, Upload, AlertCircle, Calendar, MailCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const STATUS_CONFIG = {
  'Submetida':    { color: 'bg-blue-500',   icon: Clock,         label: 'Submetida' },
  'Em análise':   { color: 'bg-orange-500', icon: Eye,           label: 'Em análise' },
  'Aprovada':     { color: 'bg-green-500',  icon: CheckCircle,   label: 'Aprovada' },
  'Rejeitada':    { color: 'bg-red-500',    icon: XCircle,       label: 'Rejeitada' },
  'Cancelada':    { color: 'bg-slate-500',  icon: XCircle,       label: 'Cancelada' },
  'Sem registo':  { color: 'bg-slate-400',  icon: FileText,      label: 'Sem registo' }
};

const ORDEM_ESTADOS = ['Submetida', 'Em análise', 'Aprovada'];
const TIPOS_DOCUMENTO = [
  'Estatutos', 'Regulamento Interno', 'Relatório de Atividades',
  'Plano Atividades', 'Relatório de Contas', 'Balanço',
  'Demonstração de Resultados', 'Certificado de Registo',
  'Acordo de Cooperação', 'Alvará/Licença', 'Seguro',
  'Declaração IRS/IRC', 'Certidão Permanente', 'Comprovativo NIF',
  'Comprovativo IBAN', 'Declaração Utilidade Pública', 'Outro'
];

// Barra de progresso do workflow
function WorkflowProgressBar({ status, workflowConfig }) {
  const estados = workflowConfig?.estados?.filter(e => !['Rejeitada','Cancelada','Sem registo'].includes(e.id))
    .sort((a, b) => a.ordem - b.ordem) || [];
  const isFinal = ['Aprovada','Rejeitada','Cancelada'].includes(status);
  const isNegativo = ['Rejeitada','Cancelada'].includes(status);

  return (
    <div className="mb-4">
      <div className="flex items-center gap-1">
        {estados.map((e, idx) => {
          const idxAtual = estados.findIndex(x => x.id === status);
          const passado  = idx < idxAtual || status === 'Aprovada';
          const atual    = e.id === status;
          return (
            <React.Fragment key={e.id}>
              <div className={`flex flex-col items-center gap-1 ${idx === 0 ? '' : ''}`} style={{ minWidth: 70 }}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 transition-all ${
                  passado ? 'bg-green-500 border-green-500' :
                  atual ? (isNegativo ? 'bg-red-500 border-red-500' : 'border-[#0066CC] bg-[#0066CC]') :
                  'bg-slate-200 border-slate-300 text-slate-500'}`}>
                  {passado ? '✓' : e.ordem}
                </div>
                <span className={`text-[10px] text-center leading-tight ${atual ? 'font-semibold text-[#0066CC]' : 'text-slate-500'}`}>{e.nome}</span>
              </div>
              {idx < estados.length - 1 && (
                <div className={`flex-1 h-0.5 mb-4 ${passado ? 'bg-green-500' : 'bg-slate-200'}`}></div>
              )}
            </React.Fragment>
          );
        })}
        {isNegativo && (
          <>
            <div className="flex-1 h-0.5 mb-4 bg-red-200"></div>
            <div className="flex flex-col items-center gap-1" style={{ minWidth: 70 }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center bg-red-500 border-2 border-red-500 text-white text-xs font-bold">✕</div>
              <span className="text-[10px] font-semibold text-red-600 text-center">{status}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function CandidaturasPage() {
  const [candidaturas, setCandidaturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidatura, setSelectedCandidatura] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [novoStatus, setNovoStatus] = useState('');
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [workflowConfig, setWorkflowConfig] = useState(null);
  const [activeModalTab, setActiveModalTab] = useState('processo');
  const [docsCandidatura, setDocsCandidatura] = useState({ documentos: [], pendentes: [] });
  const [solicitarDocOpen, setSolicitarDocOpen] = useState(false);
  const [novoDoc, setNovoDoc] = useState({ tipo: '', descricao: '', data_limite: '' });
  // Filtro de urgência de documentos
  const [filtroAlertas, setFiltroAlertas] = useState(false);
  const [alertasPorInstituicao, setAlertasPorInstituicao] = useState({}); // { inst_id: { urgencia, count } }

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon for column
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronsUpDown className="w-4 h-4 text-slate-400 ml-1" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-[#0066CC] ml-1" />
      : <ChevronDown className="w-4 h-4 text-[#0066CC] ml-1" />;
  };

  useEffect(() => {
    loadCandidaturas();
    loadWorkflow();
    loadAlertasDocumentos();
  }, [filterStatus]);

  const loadWorkflow = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/configuracoes/workflows/entidade/candidaturas`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      if (resp.ok) setWorkflowConfig(await resp.json());
    } catch (e) { /* silencioso — workflow opcional */ }
  };

  const loadAlertasDocumentos = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/documentos-aviso`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      if (!resp.ok) return;
      const data = await resp.json();
      // Construir mapa: instituicao_id -> { urgenciaMax, count }
      const mapa = {};
      const prioridade = { expirado: 3, critico: 2, aviso: 1 };
      for (const aviso of (data.avisos || [])) {
        const id = aviso.instituicao_id;
        if (!id) continue;
        if (!mapa[id] || prioridade[aviso.urgencia] > prioridade[mapa[id].urgenciaMax]) {
          mapa[id] = { urgenciaMax: aviso.urgencia, count: 0 };
        }
        mapa[id].count++;
      }
      setAlertasPorInstituicao(mapa);
    } catch (e) { /* silencioso */ }
  };

  const loadCandidaturas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus !== 'Todos') params.append('status', filterStatus);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/candidaturas?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (response.ok) {
        const data = await response.json();
        setCandidaturas(data);
      }
    } catch (error) {
      console.error('Error loading candidaturas:', error);
      toast.error('Erro ao carregar candidaturas');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (candidatura) => {
    try {
      const token = localStorage.getItem('token');
      const [detailResp, docsResp] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/candidaturas/${candidatura.id}`,
          { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/candidaturas/${candidatura.id}/documentos`,
          { headers: { 'Authorization': `Bearer ${token}` }})
      ]);

      if (detailResp.ok) {
        const data = await detailResp.json();
        setSelectedCandidatura(data);
        setNovoStatus('');  // Limpar — utilizador escolhe explicitamente o destino
        setNotas('');
        setActiveModalTab('processo');
        setSolicitarDocOpen(false);
        setNovoDoc({ tipo: '', descricao: '', data_limite: '' });
      }

      if (docsResp.ok) {
        setDocsCandidatura(await docsResp.json());
      } else {
        setDocsCandidatura({ documentos: [], pendentes: [] });
      }

      setModalOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleSolicitarDoc = async () => {
    if (!novoDoc.tipo) { toast.error('Selecione o tipo de documento'); return; }
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portal/candidaturas/${selectedCandidatura.id}/solicitar-documento`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(novoDoc)
        }
      );
      if (resp.ok) {
        toast.success('Documento solicitado com sucesso');
        setSolicitarDocOpen(false);
        setNovoDoc({ tipo: '', descricao: '', data_limite: '' });
        const docsResp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/portal/candidaturas/${selectedCandidatura.id}/documentos`,
          { headers: { 'Authorization': `Bearer ${token}` }}
        );
        if (docsResp.ok) setDocsCandidatura(await docsResp.json());
      } else {
        toast.error('Erro ao solicitar documento');
      }
    } catch (e) {
      toast.error('Erro ao solicitar documento');
    }
  };

  const handleMarcarDocRecebido = async (docId) => {
    if (!selectedCandidatura?.instituicao_id) return;
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/instituicoes/${selectedCandidatura.instituicao_id}/documentos/${docId}/entregue`,
        { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }}
      );
      if (resp.ok) {
        toast.success('Documento marcado como recebido');
        const docsResp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/portal/candidaturas/${selectedCandidatura.id}/documentos`,
          { headers: { 'Authorization': `Bearer ${token}` }}
        );
        if (docsResp.ok) setDocsCandidatura(await docsResp.json());
      } else {
        toast.error('Erro ao marcar documento');
      }
    } catch (e) {
      toast.error('Erro');
    }
  };

  const handleUpdateStatus = async () => {
    if (!novoStatus) {
      toast.warning('Selecione o novo estado de destino');
      return;
    }
    if (novoStatus === selectedCandidatura.status) {
      toast.warning('Selecione um estado diferente do atual');
      return;
    }

    if (['Rejeitada', 'Cancelada'].includes(novoStatus) && !notas) {
      toast.error('O motivo é obrigatório para este estado');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/candidaturas/${selectedCandidatura.id}/status?status=${encodeURIComponent(novoStatus)}&notas=${encodeURIComponent(notas)}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        // Auto-solicitar documentos obrigatórios do novo estado que ainda não foram pedidos
        const estadoDestino = workflowConfig?.estados?.find(e => e.id === novoStatus);
        const docsObrigatorios = estadoDestino?.documentos_obrigatorios || [];
        let docsAutoSolicitados = [];

        if (docsObrigatorios.length > 0) {
          const docsPendentesActuais = docsCandidatura.pendentes || [];
          const tiposPendentes = docsPendentesActuais.map(d => d.tipo);
          const docsASolicitar = docsObrigatorios.filter(d => !tiposPendentes.includes(d));

          for (const tipoDoc of docsASolicitar) {
            try {
              const solResp = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/portal/candidaturas/${selectedCandidatura.id}/solicitar-documento`,
                {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    tipo: tipoDoc,
                    descricao: `Documento obrigatório para o estado "${novoStatus}"`,
                    obrigatorio: true
                  })
                }
              );
              if (solResp.ok) docsAutoSolicitados.push(tipoDoc);
            } catch (_) { /* continuar sem bloquear */ }
          }
        }

        if (docsAutoSolicitados.length > 0) {
          toast.success(
            `Estado → ${novoStatus}. Documentos solicitados automaticamente: ${docsAutoSolicitados.join(', ')}`
          );
        } else {
          toast.success(`Candidatura atualizada para ${novoStatus}`);
        }

        setModalOpen(false);
        loadCandidaturas();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao atualizar');
      }
    } catch (error) {
      toast.error('Erro ao atualizar candidatura');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCandidaturas = candidaturas.filter(c => {
    const matchesSearch = (
      c.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.nif?.includes(searchTerm)
    );
    const matchesAlertas = !filtroAlertas || !!alertasPorInstituicao[c.instituicao_id];
    return matchesSearch && matchesAlertas;
  });

  // Sort candidaturas
  const sortedCandidaturas = [...filteredCandidaturas].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    // Handle null/undefined
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    
    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const stats = {
    total: candidaturas.length,
    submetidas: candidaturas.filter(c => c.status === 'Submetida').length,
    emAnalise: candidaturas.filter(c => c.status === 'Em análise').length,
    aprovadas: candidaturas.filter(c => c.status === 'Aprovada').length,
    rejeitadas: candidaturas.filter(c => c.status === 'Rejeitada').length,
    canceladas: candidaturas.filter(c => c.status === 'Cancelada').length,
    comAlertas: candidaturas.filter(c => !!alertasPorInstituicao[c.instituicao_id]).length
  };

  return (
    <div className="space-y-6" data-testid="candidaturas-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Candidaturas de Instituições</h1>
          <p className="text-slate-500 dark:text-slate-400">Gestão de candidaturas à rede ENTRAJUDA</p>
        </div>
        <Button onClick={loadCandidaturas} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('Todos')}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
            <p className="text-xs text-slate-500">Total</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-blue-500" onClick={() => setFilterStatus('Submetida')}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.submetidas}</p>
            <p className="text-xs text-slate-500">Submetidas</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-orange-500" onClick={() => setFilterStatus('Em análise')}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{stats.emAnalise}</p>
            <p className="text-xs text-slate-500">Em análise</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-green-500" onClick={() => setFilterStatus('Aprovada')}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.aprovadas}</p>
            <p className="text-xs text-slate-500">Aprovadas</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-red-500" onClick={() => setFilterStatus('Rejeitada')}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.rejeitadas}</p>
            <p className="text-xs text-slate-500">Rejeitadas</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-slate-400" onClick={() => setFilterStatus('Cancelada')}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-slate-500">{stats.canceladas}</p>
            <p className="text-xs text-slate-500">Canceladas</p>
          </CardContent>
        </Card>
        {/* Card "Com Alertas" */}
        <Card
          className={`cursor-pointer hover:shadow-md transition-all border-l-4 border-l-red-500 ${filtroAlertas ? 'ring-2 ring-red-400 bg-red-50/50 dark:bg-red-900/10' : ''} ${stats.comAlertas === 0 ? 'opacity-50 cursor-default' : ''}`}
          onClick={() => stats.comAlertas > 0 && setFiltroAlertas(v => !v)}
          data-testid="card-com-alertas"
        >
          <CardContent className="pt-4 text-center relative">
            {filtroAlertas && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            )}
            <div className="flex items-center justify-center gap-1">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <p className="text-2xl font-bold text-red-600">{stats.comAlertas}</p>
            </div>
            <p className="text-xs text-slate-500">Com Alertas</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por nome, email ou NIF..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[200px]" data-testid="status-filter">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Todos">Todos os status</SelectItem>
                <SelectItem value="Submetida">Submetida</SelectItem>
                <SelectItem value="Em análise">Em análise</SelectItem>
                <SelectItem value="Aprovada">Aprovada</SelectItem>
                <SelectItem value="Rejeitada">Rejeitada</SelectItem>
                <SelectItem value="Cancelada">Cancelada</SelectItem>
                <SelectItem value="Sem registo">Sem registo</SelectItem>
              </SelectContent>
            </Select>
            {/* Filtro rápido de alertas */}
            <button
              onClick={() => setFiltroAlertas(v => !v)}
              disabled={stats.comAlertas === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                filtroAlertas
                  ? 'bg-red-500 text-white border-red-500 shadow-sm'
                  : stats.comAlertas > 0
                  ? 'bg-white dark:bg-slate-800 text-red-600 border-red-300 hover:bg-red-50'
                  : 'bg-slate-50 dark:bg-slate-800 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
              }`}
              data-testid="filtro-alertas-btn"
            >
              <AlertCircle className="w-4 h-4" />
              Com alertas
              {stats.comAlertas > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${filtroAlertas ? 'bg-white text-red-600' : 'bg-red-500 text-white'}`}>
                  {stats.comAlertas}
                </span>
              )}
            </button>
          </div>
          {/* Chip activo */}
          {filtroAlertas && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-slate-500">Filtro activo:</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium border border-red-200">
                <AlertCircle className="w-3 h-3" />
                Documentos em alerta
                <button onClick={() => setFiltroAlertas(false)} className="ml-1 hover:text-red-900 font-bold">×</button>
              </span>
              <span className="text-xs text-slate-400">{filteredCandidaturas.length} resultado(s)</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('nome')}
                  >
                    <div className="flex items-center">
                      Instituição {getSortIcon('nome')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('tipo_entidade')}
                  >
                    <div className="flex items-center">
                      Tipo {getSortIcon('tipo_entidade')}
                    </div>
                  </TableHead>
                  <TableHead>NIF</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('distrito_nome')}
                  >
                    <div className="flex items-center">
                      Distrito {getSortIcon('distrito_nome')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('data_submissao')}
                  >
                    <div className="flex items-center">
                      Data {getSortIcon('data_submissao')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status {getSortIcon('status')}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCandidaturas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Nenhuma candidatura encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedCandidaturas.map((cand) => (
                    <TableRow 
                      key={cand.id} 
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                      onClick={() => openDetail(cand)}
                      data-testid={`candidatura-row-${cand.id}`}
                    >
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium dark:text-white">{cand.nome}</p>
                            {/* Badge de urgência de documentos */}
                            {alertasPorInstituicao[cand.instituicao_id] && (() => {
                              const alerta = alertasPorInstituicao[cand.instituicao_id];
                              return alerta.urgenciaMax === 'expirado' ? (
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300 shrink-0" data-testid={`alerta-doc-${cand.id}`}>
                                  <AlertCircle className="w-2.5 h-2.5" />DOC EXPIRADO
                                </span>
                              ) : alerta.urgenciaMax === 'critico' ? (
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-300 shrink-0" data-testid={`alerta-doc-${cand.id}`}>
                                  <AlertTriangle className="w-2.5 h-2.5" />DOC CRÍTICO
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 shrink-0" data-testid={`alerta-doc-${cand.id}`}>
                                  <AlertCircle className="w-2.5 h-2.5" />DOC {alerta.count}
                                </span>
                              );
                            })()}
                          </div>
                          <p className="text-xs text-slate-500">{cand.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{cand.tipo_entidade}</TableCell>
                      <TableCell className="font-mono text-sm">{cand.nif}</TableCell>
                      <TableCell>{cand.distrito_nome || '-'}</TableCell>
                      <TableCell className="text-slate-500">
                        {cand.data_submissao ? new Date(cand.data_submissao).toLocaleDateString('pt-PT') : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_CONFIG[cand.status]?.color || 'bg-slate-500'}>
                          {cand.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(cand); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-[#0066CC]" />
              {selectedCandidatura?.nome}
              {selectedCandidatura?.status && (
                <Badge className={STATUS_CONFIG[selectedCandidatura.status]?.color}>
                  {selectedCandidatura.status}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Gestão da candidatura à rede ENTRAJUDA
            </DialogDescription>
          </DialogHeader>

          {selectedCandidatura && (
            <Tabs value={activeModalTab} onValueChange={setActiveModalTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="processo" className="flex items-center gap-1.5" data-testid="tab-processo">
                  <Settings className="w-4 h-4" />
                  Processo
                </TabsTrigger>
                <TabsTrigger value="dados" className="flex items-center gap-1.5" data-testid="tab-dados">
                  <Building2 className="w-4 h-4" />
                  Dados
                </TabsTrigger>
                <TabsTrigger value="historico" className="flex items-center gap-1.5" data-testid="tab-historico">
                  <History className="w-4 h-4" />
                  Histórico
                </TabsTrigger>
              </TabsList>

              {/* ===== TAB PROCESSO ===== */}
              <TabsContent value="processo" className="space-y-5 mt-4">
                {/* Barra de progresso do workflow */}
                <WorkflowProgressBar status={selectedCandidatura.status} workflowConfig={workflowConfig} />

                {/* Informação do estado actual no workflow */}
                {workflowConfig && (() => {
                  const estadoAtual = workflowConfig.estados?.find(e => e.id === selectedCandidatura.status);
                  const transicoesPossiveis = workflowConfig.transicoes?.filter(t => t.de === selectedCandidatura.status) || [];
                  return estadoAtual ? (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700 space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: estadoAtual.cor }}></div>
                        <span className="font-semibold text-sm dark:text-white">Estado actual: {estadoAtual.nome}</span>
                        <Link to="/configuracoes/workflows" className="ml-auto text-xs text-[#0066CC] flex items-center gap-1 hover:underline">
                          <Settings className="w-3 h-3" />Configurar workflow
                        </Link>
                      </div>
                      {estadoAtual.descricao && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
                          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-400" />
                          {estadoAtual.descricao}
                        </p>
                      )}
                      {transicoesPossiveis.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Transições possíveis:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {transicoesPossiveis.map((t, i) => {
                              const paraEstado = workflowConfig.estados?.find(e => e.id === t.para);
                              return (
                                <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border"
                                  style={{ borderColor: paraEstado?.cor + '60', color: paraEstado?.cor, backgroundColor: paraEstado?.cor + '15' }}>
                                  {t.acao} <ArrowRight className="w-3 h-3" /> {t.para}
                                  {t.requer_notas && <span className="text-[10px] opacity-70">(notas obrigatórias)</span>}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}

                {/* Checklist de Documentos */}
                {(() => {
                  const estadoAtual = workflowConfig?.estados?.find(e => e.id === selectedCandidatura.status);
                  const docsObrigatorios = estadoAtual?.documentos_obrigatorios || [];
                  return (
                    <div className="border rounded-lg dark:border-slate-700">
                      <div className="flex items-center justify-between p-3 border-b dark:border-slate-700 bg-amber-50/60 dark:bg-amber-900/10 rounded-t-lg">
                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Documentos do Processo
                          {docsObrigatorios.length > 0 && (
                            <Badge className="bg-amber-500 text-xs ml-1">{docsObrigatorios.length} obrigatório(s)</Badge>
                          )}
                        </p>
                        {!['Aprovada', 'Rejeitada', 'Cancelada'].includes(selectedCandidatura.status) && (
                          <Button size="sm" variant="outline" onClick={() => setSolicitarDocOpen(v => !v)}
                            className="text-xs h-7 text-[#0066CC] border-[#0066CC] hover:bg-[#0066CC]/10"
                            data-testid="btn-solicitar-doc">
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            Solicitar Documento
                          </Button>
                        )}
                      </div>

                      {/* Formulário inline para solicitar documento */}
                      {solicitarDocOpen && (
                        <div className="p-4 border-b dark:border-slate-700 bg-blue-50/40 dark:bg-blue-900/10 space-y-3">
                          <p className="text-xs font-semibold text-[#0066CC]">Solicitar novo documento à instituição:</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-slate-500">Tipo de Documento *</Label>
                              <Select value={novoDoc.tipo} onValueChange={v => setNovoDoc(prev => ({ ...prev, tipo: v }))}>
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Selecionar tipo..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {TIPOS_DOCUMENTO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">Prazo Limite</Label>
                              <Input type="date" value={novoDoc.data_limite || ''} onChange={e => setNovoDoc(prev => ({ ...prev, data_limite: e.target.value }))} className="h-8 text-xs" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Descrição / Instruções</Label>
                            <Textarea value={novoDoc.descricao} onChange={e => setNovoDoc(prev => ({ ...prev, descricao: e.target.value }))} rows={2} className="text-xs" placeholder="Detalhe o que é necessário..." />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => setSolicitarDocOpen(false)} className="h-7 text-xs">Cancelar</Button>
                            <Button size="sm" onClick={handleSolicitarDoc} className="h-7 text-xs bg-[#0066CC] hover:bg-[#0055AA]" data-testid="btn-confirmar-solicitar">
                              Solicitar
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="divide-y dark:divide-slate-700">
                        {/* Documentos obrigatórios do workflow ainda não solicitados */}
                        {docsObrigatorios.map(tipoDoc => {
                          const jaSolicitado = docsCandidatura.pendentes?.find(d => d.tipo === tipoDoc);
                          if (jaSolicitado) return null;
                          return (
                            <div key={tipoDoc} className="flex items-center justify-between p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full border-2 border-amber-400 bg-amber-50 flex items-center justify-center shrink-0">
                                  <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium dark:text-white">{tipoDoc}</p>
                                  <p className="text-xs text-amber-600 dark:text-amber-400">Obrigatório — Não solicitado</p>
                                </div>
                              </div>
                              {!['Aprovada', 'Rejeitada', 'Cancelada'].includes(selectedCandidatura.status) && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600 border-amber-400 hover:bg-amber-50"
                                  onClick={() => { setNovoDoc(prev => ({ ...prev, tipo: tipoDoc })); setSolicitarDocOpen(true); }}>
                                  <Plus className="w-3 h-3 mr-1" />Solicitar
                                </Button>
                              )}
                            </div>
                          );
                        })}

                        {/* Documentos já solicitados / pendentes */}
                        {docsCandidatura.pendentes?.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-3" data-testid={`doc-pendente-${doc.id}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${doc.entregue ? 'bg-green-500' : 'bg-orange-100 border-2 border-orange-400'}`}>
                                {doc.entregue
                                  ? <CheckCircle className="w-3.5 h-3.5 text-white" />
                                  : <Clock className="w-3.5 h-3.5 text-orange-500" />
                                }
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium dark:text-white">{doc.tipo}</p>
                                  {/* Badge de urgência */}
                                  {!doc.entregue && doc.data_limite && (() => {
                                    const dias = Math.round((new Date(doc.data_limite) - new Date()) / (1000 * 60 * 60 * 24));
                                    if (dias < 0) return <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300"><AlertCircle className="w-2.5 h-2.5" />EXPIRADO</span>;
                                    if (dias <= 7) return <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-300"><AlertTriangle className="w-2.5 h-2.5" />{dias}d</span>;
                                    if (dias <= 30) return <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"><Clock className="w-2.5 h-2.5" />{dias}d</span>;
                                    return null;
                                  })()}
                                </div>
                                <p className={`text-xs ${doc.entregue ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                                  {doc.entregue
                                    ? `Recebido${doc.data_entrega ? ' em ' + new Date(doc.data_entrega).toLocaleDateString('pt-PT') : ''}`
                                    : `Aguarda entrega${doc.data_limite ? ' · Prazo: ' + new Date(doc.data_limite).toLocaleDateString('pt-PT') : ''}`
                                  }
                                </p>
                                {doc.descricao && <p className="text-xs text-slate-400 mt-0.5">{doc.descricao}</p>}
                              </div>
                            </div>
                            {!doc.entregue && !['Aprovada', 'Rejeitada', 'Cancelada'].includes(selectedCandidatura.status) && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-green-600 border-green-400 hover:bg-green-50"
                                onClick={() => handleMarcarDocRecebido(doc.id)}
                                data-testid={`btn-marcar-recebido-${doc.id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" />Recebido
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Documentos legais da candidatura */}
                        {docsCandidatura.documentos?.slice(0, 5).map(doc => (
                          <div key={doc.id} className="flex items-center gap-3 p-3 opacity-75">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                              doc.status_validacao === 'Aprovado' ? 'bg-green-500' :
                              doc.status_validacao === 'Rejeitado' ? 'bg-red-500' : 'bg-blue-100 border-2 border-blue-400'}`}>
                              {doc.status_validacao === 'Aprovado' ? <CheckCircle className="w-3.5 h-3.5 text-white" /> :
                               doc.status_validacao === 'Rejeitado' ? <XCircle className="w-3.5 h-3.5 text-white" /> :
                               <FileText className="w-3 h-3 text-blue-500" />}
                            </div>
                            <div>
                              <p className="text-sm font-medium dark:text-white">{doc.tipo}</p>
                              <p className={`text-xs ${
                                doc.status_validacao === 'Aprovado' ? 'text-green-600' :
                                doc.status_validacao === 'Rejeitado' ? 'text-red-600' : 'text-blue-600'
                              }`}>{doc.status_validacao || 'Submetido'}</p>
                            </div>
                          </div>
                        ))}

                        {/* Estado vazio */}
                        {docsObrigatorios.length === 0 && !docsCandidatura.pendentes?.length && !docsCandidatura.documentos?.length && (
                          <div className="p-6 text-center text-slate-500">
                            <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">Nenhum documento registado</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Atualizar estado da candidatura */}
                {!['Aprovada', 'Rejeitada', 'Cancelada'].includes(selectedCandidatura.status) ? (
                  <div className="border rounded-lg dark:border-slate-700 p-4 space-y-4">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Atualizar Estado</p>
                    <Select value={novoStatus} onValueChange={setNovoStatus}>
                      <SelectTrigger data-testid="novo-status-select">
                        <SelectValue placeholder="Selecione o novo estado..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const transicoes = workflowConfig?.transicoes?.filter(t => t.de === selectedCandidatura.status);
                          if (transicoes && transicoes.length > 0) {
                            return transicoes.map(t => (
                              <SelectItem key={t.para} value={t.para}>{t.acao} → {t.para}</SelectItem>
                            ));
                          }
                          // Fallback: todos os estados possíveis
                          return (
                            <>
                              <SelectItem value="Em análise">Em análise</SelectItem>
                              <SelectItem value="Aprovada">Aprovada</SelectItem>
                              <SelectItem value="Rejeitada">Rejeitada</SelectItem>
                              <SelectItem value="Cancelada">Cancelada</SelectItem>
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>

                    {/* Aviso de documentos obrigatórios — estado actual + destino */}
                    {novoStatus && (() => {
                      // Verificar docs obrigatórios do estado ACTUAL (podem ser necessários antes de avançar)
                      const estadoActual = workflowConfig?.estados?.find(e => e.id === selectedCandidatura.status);
                      const estadoDestino = workflowConfig?.estados?.find(e => e.id === novoStatus);
                      // Unir documentos obrigatórios do estado actual e do destino
                      const docsActual = estadoActual?.documentos_obrigatorios || [];
                      const docsDestino = estadoDestino?.documentos_obrigatorios || [];
                      const docsObrigatorios = [...new Set([...docsActual, ...docsDestino])];
                      if (!docsObrigatorios.length) return null;
                      const docsPendentes = docsCandidatura.pendentes || [];
                      const docsLegais = docsCandidatura.documentos || [];
                      const docsEntregues = [
                        ...docsPendentes.filter(d => d.entregue).map(d => d.tipo),
                        ...docsLegais.filter(d => d.status_validacao === 'Aprovado').map(d => d.tipo)
                      ];
                      const docsEmFalta = docsObrigatorios.filter(doc => !docsEntregues.includes(doc));
                      if (!docsEmFalta.length) return (
                        <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded text-xs text-green-700 border border-green-200">
                          <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                          Todos os documentos obrigatórios estão presentes
                        </div>
                      );
                      return (
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 text-sm">
                          <p className="font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5 mb-2">
                            <AlertTriangle className="w-4 h-4" />
                            Documentos em falta para esta transição:
                          </p>
                          <ul className="space-y-0.5">
                            {docsEmFalta.map(doc => (
                              <li key={doc} className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                                <AlertCircle className="w-3 h-3 shrink-0" />
                                {doc}
                              </li>
                            ))}
                          </ul>
                          <p className="text-xs text-amber-600 mt-2 opacity-80">Pode continuar, mas recomenda-se solicitar os documentos em falta.</p>
                        </div>
                      );
                    })()}
                    {novoStatus === 'Aprovada' && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg text-sm text-green-700 dark:text-green-400 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>Ao aprovar: Estado da Instituição → <strong>Ativa</strong>. Será gerado código provisório.</span>
                      </div>
                    )}
                    {(novoStatus === 'Rejeitada' || novoStatus === 'Cancelada') && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
                        <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>Estado da Instituição → <strong>Inativa</strong>. Motivo obrigatório.</span>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-slate-500">
                        Motivo {(['Rejeitada', 'Cancelada'].includes(novoStatus) || workflowConfig?.transicoes?.find(t => t.de === selectedCandidatura.status && t.para === novoStatus)?.requer_notas)
                          ? '(obrigatório)' : '(opcional)'}
                      </Label>
                      <Textarea
                        value={notas}
                        onChange={(e) => setNotas(e.target.value)}
                        placeholder="Adicionar notas sobre esta atualização..."
                        rows={3}
                        data-testid="notas-textarea"
                      />
                    </div>
                    <Button onClick={handleUpdateStatus} disabled={submitting} className="w-full bg-[#0066CC] hover:bg-[#0055AA]" data-testid="update-status-btn">
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Confirmar Atualização de Estado
                    </Button>
                  </div>
                ) : (
                  <div className={`p-4 rounded-lg border ${
                    selectedCandidatura.status === 'Aprovada'
                      ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                      : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                  }`}>
                    <div className="flex items-center gap-2">
                      {selectedCandidatura.status === 'Aprovada'
                        ? <CheckCircle className="w-5 h-5 text-green-600" />
                        : <XCircle className="w-5 h-5 text-red-600" />}
                      <span className={`font-medium ${selectedCandidatura.status === 'Aprovada' ? 'text-green-800 dark:text-green-400' : 'text-red-800 dark:text-red-400'}`}>
                        Candidatura {selectedCandidatura.status}
                      </span>
                    </div>
                    {selectedCandidatura.data_aprovacao && (
                      <p className="text-sm mt-2 text-slate-500">
                        Em: {new Date(selectedCandidatura.data_aprovacao).toLocaleDateString('pt-PT')}
                        {selectedCandidatura.aprovado_por_nome && ` por ${selectedCandidatura.aprovado_por_nome}`}
                      </p>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* ===== TAB DADOS ===== */}
              <TabsContent value="dados" className="space-y-5 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Tipo de Entidade</Label>
                    <p className="font-medium dark:text-white">{selectedCandidatura.tipo_entidade}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">NIF</Label>
                    <p className="font-mono dark:text-white">{selectedCandidatura.nif}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <p className="dark:text-white">{selectedCandidatura.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Telefone</Label>
                    <p className="dark:text-white">{selectedCandidatura.telefone || '-'}</p>
                  </div>
                  {selectedCandidatura.website && (
                    <div>
                      <Label className="text-xs text-slate-500">Website</Label>
                      <p className="dark:text-white">{selectedCandidatura.website}</p>
                    </div>
                  )}
                  {selectedCandidatura.ano_inicio_atividade && (
                    <div>
                      <Label className="text-xs text-slate-500">Ano de Início</Label>
                      <p className="dark:text-white">{selectedCandidatura.ano_inicio_atividade}</p>
                    </div>
                  )}
                </div>

                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <Label className="text-sm font-semibold mb-3 block text-slate-700 dark:text-slate-300">Localização</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Distrito / Concelho</Label>
                      <p className="dark:text-white">{selectedCandidatura.distrito_nome} / {selectedCandidatura.concelho_nome || '-'}</p>
                    </div>
                    {selectedCandidatura.freguesia && (
                      <div>
                        <Label className="text-xs text-slate-500">Freguesia</Label>
                        <p className="dark:text-white">{selectedCandidatura.freguesia}</p>
                      </div>
                    )}
                    {selectedCandidatura.morada && (
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Morada</Label>
                        <p className="dark:text-white">{selectedCandidatura.morada}</p>
                      </div>
                    )}
                    {selectedCandidatura.codigo_postal && (
                      <div>
                        <Label className="text-xs text-slate-500">Código Postal</Label>
                        <p className="font-mono dark:text-white">{selectedCandidatura.codigo_postal}</p>
                      </div>
                    )}
                  </div>
                </div>

                {(selectedCandidatura.presidente_nome || selectedCandidatura.presidente_email) && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <Label className="text-sm font-semibold mb-3 block text-blue-700 dark:text-blue-300">Presidente / Representante Legal</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label className="text-xs text-slate-500">Nome</Label><p className="dark:text-white">{selectedCandidatura.presidente_nome || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Cargo</Label><p className="dark:text-white">{selectedCandidatura.presidente_cargo || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Telefone</Label><p className="dark:text-white">{selectedCandidatura.presidente_telefone || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Email</Label><p className="dark:text-white">{selectedCandidatura.presidente_email || '-'}</p></div>
                    </div>
                  </div>
                )}

                {(selectedCandidatura.contacto_nome || selectedCandidatura.contacto_email) && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <Label className="text-sm font-semibold mb-3 block text-green-700 dark:text-green-300">Pessoa de Contacto</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label className="text-xs text-slate-500">Nome</Label><p className="dark:text-white">{selectedCandidatura.contacto_nome || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Cargo</Label><p className="dark:text-white">{selectedCandidatura.contacto_cargo || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Telefone</Label><p className="dark:text-white">{selectedCandidatura.contacto_telefone || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Email</Label><p className="dark:text-white">{selectedCandidatura.contacto_email || '-'}</p></div>
                    </div>
                  </div>
                )}

                {(selectedCandidatura.missao || selectedCandidatura.populacao_apoiada || selectedCandidatura.area_geografica) && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <Label className="text-sm font-semibold mb-3 block text-amber-700 dark:text-amber-300">Sobre a Instituição</Label>
                    <div className="space-y-3">
                      {selectedCandidatura.missao && <div><Label className="text-xs text-slate-500">Missão</Label><p className="dark:text-white text-sm">{selectedCandidatura.missao}</p></div>}
                      {selectedCandidatura.populacao_apoiada && <div><Label className="text-xs text-slate-500">População Apoiada</Label><p className="dark:text-white text-sm">{selectedCandidatura.populacao_apoiada}</p></div>}
                      {selectedCandidatura.area_geografica && <div><Label className="text-xs text-slate-500">Área Geográfica</Label><p className="dark:text-white text-sm">{selectedCandidatura.area_geografica}</p></div>}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <Label className="text-xs text-slate-500">Colaboradores Remunerados</Label>
                    <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                      {selectedCandidatura.num_colaboradores_remunerados || selectedCandidatura.num_colaboradores || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                    <Label className="text-xs text-slate-500">Voluntários Regulares</Label>
                    <p className="text-2xl font-bold text-pink-700 dark:text-pink-300">
                      {selectedCandidatura.num_voluntarios_regulares || selectedCandidatura.num_voluntarios || 0}
                    </p>
                  </div>
                </div>

                {selectedCandidatura.areas_interesse?.length > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Áreas de Interesse</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedCandidatura.areas_interesse.map((area, i) => (
                        <Badge key={i} variant="outline" className="bg-slate-100 dark:bg-slate-700">{area}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4 dark:border-slate-700">
                  <div>
                    <Label className="text-xs text-slate-500">Data de Submissão</Label>
                    <p className="dark:text-white">
                      {selectedCandidatura.data_submissao ? new Date(selectedCandidatura.data_submissao).toLocaleString('pt-PT') : '-'}
                    </p>
                  </div>
                  {selectedCandidatura.codigo_provisorio && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <Label className="text-xs text-slate-500">Código Provisório</Label>
                      <p className="font-mono font-bold text-amber-600">{selectedCandidatura.codigo_provisorio}</p>
                    </div>
                  )}
                  {selectedCandidatura.codigo_ea && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <Label className="text-xs text-slate-500">Código EA (Definitivo)</Label>
                      <p className="font-mono font-bold text-[#0066CC]">{selectedCandidatura.codigo_ea}</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* ===== TAB HISTÓRICO ===== */}
              <TabsContent value="historico" className="space-y-5 mt-4">
                {selectedCandidatura.verificacoes && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <Label className="text-xs text-slate-500 mb-3 block font-semibold">Verificações Automáticas</Label>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2">
                        {selectedCandidatura.verificacoes.nif_valido ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                        <span className="text-sm">NIF Válido</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedCandidatura.verificacoes.email_valido ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                        <span className="text-sm">Email Válido</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {!selectedCandidatura.verificacoes.duplicado_possivel ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                        <span className="text-sm">Sem Duplicados</span>
                      </div>
                    </div>
                  </div>
                )}

                {selectedCandidatura.validacoes?.length > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 flex items-center gap-2 font-semibold">
                      <CheckCircle className="w-4 h-4" />
                      Validações Registadas ({selectedCandidatura.validacoes.length})
                    </Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedCandidatura.validacoes.map((v, i) => (
                        <div key={i} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded text-sm border-l-2 border-blue-500">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-medium text-blue-700 dark:text-blue-400">{v.user_nome}</span>
                            <span className="text-slate-500">{new Date(v.data).toLocaleString('pt-PT')}</span>
                          </div>
                          <p className="text-slate-700 dark:text-slate-300">{v.status_anterior} → {v.status_novo}</p>
                          {v.notas && <p className="text-xs text-slate-500 mt-1">{v.notas}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCandidatura.historico_notas?.length > 0 ? (
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 flex items-center gap-2 font-semibold">
                      <History className="w-4 h-4" />
                      Histórico de Notas ({selectedCandidatura.historico_notas.length})
                    </Label>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {selectedCandidatura.historico_notas.map((h, i) => (
                        <div key={i} className="p-3 bg-slate-50 dark:bg-slate-800 rounded text-sm border-l-2 border-slate-300 dark:border-slate-600">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span className="font-medium">{h.user}</span>
                            <span>{new Date(h.data).toLocaleString('pt-PT')}</span>
                          </div>
                          <p className="dark:text-white">
                            <span className="text-slate-600 dark:text-slate-400">{h.status_anterior}</span>
                            {' → '}
                            <span className="font-semibold text-[#0066CC]">{h.status_novo}</span>
                          </p>
                          {h.nota && <p className="text-xs text-slate-500 mt-1 italic">"{h.nota}"</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500">
                    <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Nenhum registo de alterações ainda</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
